import { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { AlertComponent, ModalComponent } from "../../../components";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";
import { DeleteEntryDialog } from "../../../components/documents/pump/DeleteEntryDialog";
import {
  ReportForm,
  ReportFormValues,
  SUGGESTED_REPORT_TYPES,
} from "../../../components/common/ReportForm";
import DataContext from "../../../context/DataContext";
import PaginatorContext from "../../../context/PaginatorContext";
import { useFetch } from "../../../hooks/useFetch";
import { usePermissions } from "../../../hooks/usePermissions";
import { useRequest } from "../../../hooks/useRequest";
import { ClientProps, ReportProps } from "../../../interfaces/interfaces";
import { buildReportFormData } from "./reportFormData";

interface ReportsResponse {
  succes?: boolean;
  success?: boolean;
  data: ReportProps[];
}

interface Props {
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
  embedded?: boolean;
}

const filterLabelSx = {
  display: "block",
  mb: "5px",
  color: "#9CA1AB",
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".6px",
  lineHeight: 1.2,
  textTransform: "uppercase",
};

const documentFilterSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    bgcolor: "#FAFAF8",
    borderRadius: "9px",
    fontSize: 13,
    "& fieldset": { borderColor: "#E2E2DD" },
    "&:hover fieldset": { borderColor: "#C9C7BE" },
    "&.Mui-focused fieldset": {
      borderColor: "#ED8B00",
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": { py: "8px" },
};

const formatReportDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const tableHeaders = [
  "Title",
  "Client",
  "Report Type",
  "Period",
  "Source",
  "Uploaded",
  "",
];

export const ReportsAdmin = ({
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
  embedded = false,
}: Props) => {
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);
  const { clientOptions: globalClientOptions, fetchDataClient } =
    useContext(DataContext);
  const { handleRequest } = useRequest();
  const { canManageReports } = usePermissions();
  const clientOptions = clientMode ? assignedClientOptions : globalClientOptions;

  const [clientId, setClientId] = useState("");
  const [reportType, setReportType] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ReportProps | null>(null);
  const [deletingRow, setDeletingRow] = useState<ReportProps | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!clientMode) fetchDataClient();
    // Master data is intentionally loaded once per page mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    setClientId(String(assignedClientId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedClientId, clientMode]);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setSearch(searchDraft.trim()),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [searchDraft]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSuccess(false);
      setIsError(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, [isSuccess, isError]);

  const filters = useMemo(
    () => ({ clientId, reportType, search, dateFrom, dateTo }),
    [clientId, reportType, search, dateFrom, dateTo],
  );
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<ReportsResponse>(
      "report-paginate",
      paginationModel,
      undefined,
      undefined,
      filters,
    );
  const rows = data?.data || [];

  const resetPage = () =>
    fetchPaginationModel({ ...paginationModel, page: 0 });

  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId
        ? String(assignedClientId)
        : value;
    setClientId(nextClientId);
    if (clientMode) {
      onAssignedClientChange?.(nextClientId ? Number(nextClientId) : null);
    }
    resetPage();
  };

  const filterText =
    (setter: (value: string) => void) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setter(event.target.value);
      resetPage();
    };

  const hasActiveFilters = Boolean(
    reportType || searchDraft || dateFrom || dateTo,
  );
  const clearFilters = () => {
    setReportType("");
    setSearchDraft("");
    setSearch("");
    setDateFrom("");
    setDateTo("");
    resetPage();
  };

  const submitReport = async (values: ReportFormValues) => {
    setIsSubmittingReport(true);
    setFormError(null);
    try {
      const formData = buildReportFormData(values);

      const endpoint = editingRow
        ? `${process.env.REACT_APP_DEV_API}/report/${editingRow.id}`
        : `${process.env.REACT_APP_DEV_API}/report-upload`;
      const response = await fetch(endpoint, {
        method: editingRow ? "PUT" : "POST",
        body: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem("info")}` },
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.success) {
        throw new Error(result?.error || "The report could not be saved");
      }
      setIsSuccess(true);
      fetchData();
      setFormOpen(false);
      setEditingRow(null);
    } catch (submitError) {
      setFormError(
        submitError instanceof Error
          ? submitError.message
          : "The report could not be saved",
      );
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const confirmDeleteRow = async () => {
    if (!deletingRow) return;
    setDeleting(true);
    try {
      const response = await handleRequest({
        endpoint: `report/${deletingRow.id}`,
        options: { method: "DELETE" },
      });
      if (!response.success) throw new Error("Delete failed");
      setDeletingRow(null);
      fetchData();
    } catch {
      setIsError(true);
    } finally {
      setDeleting(false);
    }
  };

  const previewReport = async (row: ReportProps) => {
    const win = window.open("", "_blank");
    setIsBusy(true);
    try {
      const response = await handleRequest({
        endpoint: `report-download/${row.id}`,
        options: { method: "GET" },
      });
      if (!response.success) throw new Error("Download failed");
      const mimeType =
        response.meta?.mimeType || row.mimeType || "text/html";
      const url = window.URL.createObjectURL(
        new Blob([new Uint8Array(response.data.data).buffer], {
          type: mimeType,
        }),
      );
      if (!win) throw new Error("Preview window was blocked");
      win.opener = null;
      win.document.title = row.title;
      win.document.body.style.margin = "0";
      const iframe = win.document.createElement("iframe");
      iframe.title = row.title;
      iframe.setAttribute("sandbox", "allow-scripts");
      iframe.style.width = "100vw";
      iframe.style.height = "100vh";
      iframe.style.border = "0";
      iframe.src = url;
      iframe.addEventListener("load", () => {
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      });
      win.document.body.replaceChildren(iframe);
    } catch {
      win?.close();
      setIsError(true);
    } finally {
      setIsBusy(false);
    }
  };

  const colSpan = tableHeaders.length;

  const addReportButton =
    canManageReports && !clientMode ? (
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineRoundedIcon />}
        onClick={() => {
          setEditingRow(null);
          setFormError(null);
          setFormOpen(true);
        }}
        sx={{
          borderColor: "#E7E7E3",
          color: "#2A2F38",
          borderRadius: "9px",
          px: "15px",
          py: "8px",
          fontSize: 13,
          fontWeight: 600,
          textTransform: "none",
          "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
        }}
      >
        Add Report
      </Button>
    ) : undefined;

  return (
    <Box
      sx={
        embedded
          ? undefined
          : { minHeight: "100vh", bgcolor: "#F3F3F0", color: "#29313D" }
      }
    >
      {embedded ? null : (
        <DocumentsTopNavbar title="Reports" actions={addReportButton} />
      )}
      <Box
        sx={
          embedded
            ? undefined
            : { maxWidth: 1680, mx: "auto", p: { xs: 2, md: 3.5 } }
        }
      >
        {embedded && addReportButton ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            {addReportButton}
          </Box>
        ) : null}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            border: "1px solid #E7E7E3",
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: "16px 20px",
              display: "flex",
              alignItems: "flex-end",
              gap: "11px",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ width: { xs: "100%", sm: 190 } }}>
              <Box component="label" sx={filterLabelSx}>Client</Box>
              <Autocomplete
                size="small"
                options={clientOptions}
                value={
                  clientOptions.find(
                    (client) => String(client.id) === clientId,
                  ) || null
                }
                getOptionLabel={(client) => client.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, client) =>
                  chooseClient(client ? String(client.id) : "")
                }
                disableClearable={clientMode && clientOptions.length === 1}
                disabled={clientMode && clientOptions.length === 1}
                autoHighlight
                openOnFocus
                sx={documentFilterSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Search clients…" />
                )}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 170 } }}>
              <Box component="label" sx={filterLabelSx}>Report type</Box>
              <Autocomplete
                freeSolo
                size="small"
                options={SUGGESTED_REPORT_TYPES}
                value={reportType}
                onChange={(_, newValue) => {
                  setReportType(newValue ?? "");
                  resetPage();
                }}
                onInputChange={(_, newInputValue) => {
                  setReportType(newInputValue);
                  resetPage();
                }}
                sx={documentFilterSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="e.g. Performance" />
                )}
              />
            </Box>
            <Box sx={{ flex: "1 1 220px", minWidth: 200 }}>
              <Box component="label" sx={filterLabelSx}>Search</Box>
              <TextField
                fullWidth
                size="small"
                value={searchDraft}
                onChange={filterText(setSearchDraft)}
                placeholder="Title…"
                sx={documentFilterSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "#A6ABB5", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>From date</Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={filterText(setDateFrom)}
                sx={documentFilterSx}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 145 } }}>
              <Box component="label" sx={filterLabelSx}>To date</Box>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={filterText(setDateTo)}
                sx={documentFilterSx}
              />
            </Box>
            {hasActiveFilters ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={clearFilters}
                sx={{
                  borderColor: "#E2E2DD",
                  borderRadius: "9px",
                  fontSize: 12.5,
                  fontWeight: 650,
                  textTransform: "none",
                }}
              >
                Clear filters
              </Button>
            ) : null}
          </Box>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Reports could not be loaded.
          </Alert>
        ) : null}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E4E3DC",
            borderRadius: 2.5,
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F8F8F5" }}>
                  {tableHeaders.map((label, index) => (
                    <TableCell
                      key={`${label}-${index}`}
                      align={label === "" ? "right" : "left"}
                      sx={{
                        color: "#707782",
                        fontSize: 11,
                        letterSpacing: 0.65,
                        textTransform: "uppercase",
                        fontWeight: 850,
                        borderColor: "#EAE9E3",
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading || isBusy ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 8 }}>
                      <CircularProgress size={30} />
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoading && !isBusy && !rows.length ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 8 }}>
                      <DescriptionOutlinedIcon sx={{ fontSize: 36, color: "#B8BDC5", mb: 1 }} />
                      <Typography fontWeight={800}>No reports found</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {clientMode
                          ? "Adjust the filters to find dashboard reports."
                          : "Select a client, adjust filters, generate from Excel, or upload ready HTML."}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {!isLoading &&
                  !isBusy &&
                  rows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ "& td": { borderColor: "#EFEEE9" } }}
                    >
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2" fontWeight={850}>
                          {row.title}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.client?.name || "—"}</TableCell>
                      <TableCell>{row.reportType}</TableCell>
                      <TableCell>{row.period}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.sourceType === "html" ? "HTML uploaded" : "Excel generated"}
                          size="small"
                          sx={{
                            bgcolor: row.sourceType === "html" ? "#EDF1F4" : "#FFF2DC",
                            color: row.sourceType === "html" ? "#4D5967" : "#875100",
                            fontSize: 11.5,
                            fontWeight: 750,
                          }}
                        />
                      </TableCell>
                      <TableCell>{formatReportDate(row.date)}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => previewReport(row)}
                            title="Preview"
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                          {canManageReports && !clientMode ? (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingRow(row);
                                setFormError(null);
                                setFormOpen(true);
                              }}
                              title="Edit"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                          {canManageReports && !clientMode ? (
                            <IconButton
                              size="small"
                              onClick={() => setDeletingRow(row)}
                              title="Delete"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={Number(pagination.totalRecords || 0)}
            page={paginationModel.page}
            rowsPerPage={paginationModel.pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, page) =>
              fetchPaginationModel({ ...paginationModel, page })
            }
            onRowsPerPageChange={(event) =>
              fetchPaginationModel({
                page: 0,
                pageSize: Number(event.target.value),
              })
            }
          />
        </Paper>
      </Box>
      {formOpen ? (
        <ModalComponent
          modalContent={
            <ReportForm
              clientOptions={clientOptions}
              initialValues={
                editingRow
                  ? {
                      client: editingRow.client ?? undefined,
                      reportType: editingRow.reportType,
                      sourceType: editingRow.sourceType || "excel",
                      title: editingRow.title,
                      period: editingRow.period,
                    }
                  : undefined
              }
              submitting={isSubmittingReport}
              error={formError}
              onSubmit={submitReport}
              onCancel={() => {
                setFormOpen(false);
                setEditingRow(null);
                setFormError(null);
              }}
            />
          }
        />
      ) : null}
      <DeleteEntryDialog
        open={Boolean(deletingRow)}
        onClose={() => setDeletingRow(null)}
        onConfirm={confirmDeleteRow}
        confirming={deleting}
        title="Delete report"
        description="This will permanently remove this report. This action cannot be undone."
      />
      {isSuccess ? <AlertComponent type="success" /> : null}
      {isError ? <AlertComponent type="error" /> : null}
    </Box>
  );
};
